import { Injectable, OnDestroy } from '@angular/core';
import { createEVMClient, EIP1193Provider, getInfuraRpcUrls, MetamaskConnectEVM } from '@metamask/connect-evm';
import { ethers, Interface } from 'ethers';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { NetworksInterface, Web3TransactionConfig } from 'src/app/shared/interfaces/deposit.interface';
import { environment } from 'src/environments/environment';
import { AccountApi } from '../../apis/account.api';
import { Web3WalletSignatureInfo, Web3WalletType } from '../../interfaces/auth.interface';
import { Web3WalletAdapter } from '../../interfaces/web3-wallet.interface';
import { LocalStorageService } from '../localstorage.service';
import { ToastService } from '../toast.service';

// const INFURA_API_KEY = '36e9a0ce8a27415db90497ad6376c3be'; // TEST Project ID
const INFURA_API_KEY = '61d06ceba0344ad2bba02559fb052c24'; // PROD Project ID

@Injectable({
  providedIn: 'root',
})
export class MetaMaskService implements Web3WalletAdapter, OnDestroy {
  // 根據環境設定支援的網路 [mainnet, bsc, polygon] : [sepolia, bscTestnet, polygonMumbai]
  private supportedNetworks = environment.isOnline ? [1, 56, 137] : [11155111, 97, 80001];
  private supportedNetworkChainIds: `0x${string}`[] = environment.isOnline
    ? ['0x1', '0x38', '0x89']
    : ['0xaa36a7', '0x61', '0x13881'];

  constructor(
    private appService: AppService,
    private toastService: ToastService,
    private accountApi: AccountApi,
    private localStorageService: LocalStorageService,
  ) {
    // 初始化 MetaMask 連線
    this.walletInit();
  }

  private client!: MetamaskConnectEVM;
  private provider!: EIP1193Provider;
  private curDeepLink = '';

  private readonly _isConnected = new BehaviorSubject<boolean>(false);
  private readonly _accounts = new BehaviorSubject<string[]>([]);
  private readonly _currentChainId = new BehaviorSubject<number | string | null>(null);

  public readonly isConnected$: Observable<boolean> = this._isConnected.asObservable();
  public readonly accounts$: Observable<string[]> = this._accounts.asObservable();
  public readonly currentChainId$: Observable<number | string | null> = this._currentChainId.asObservable();

  async walletInit() {
    this.client = await createEVMClient({
      dapp: {
        name: 'PUBS.com',
        url: window.location.href,
        iconUrl: `${window.location.origin}/assets/resources/web3-wallets/dapp-logo.png`,
      },
      api: {
        supportedNetworks: {
          // use the `getInfuraRpcUrls` helper to generate a map of Infura RPC endpoints
          // ...getInfuraRpcUrls({ infuraApiKey: INFURA_API_KEY, chainIds: this.supportedNetworkChainIds }),
          ...getInfuraRpcUrls({ infuraApiKey: INFURA_API_KEY }),
        },
      },
      eventHandlers: {
        accountsChanged: accounts => this.handleAccountsChanged(accounts),
        chainChanged: chainId => this.handleChainChanged(chainId),
        connect: async () => {
          // 確保 provider 已經準備好
          await new Promise(resolve => setTimeout(resolve, 100));

          // 檢查已存在的連線
          this.checkExistingConnection();
        },
      },
      mobile: {
        useDeeplink: true,
        preferredOpenLink: (deeplink, target) => {
          // const encodedTargetUrl = encodeURIComponent(window.location.href);
          // const walletUrl = `https://link.metamask.io/dapp/${encodedTargetUrl}`;
          // window.location.href = deeplink;

          this.curDeepLink = deeplink;
          window.open(deeplink, target);
        },
      },
      debug: !environment.isOnline,
    });

    this.provider = this.client.getProvider();
  }

  /**
   * 處理帳戶變更事件
   * @param accounts - 新的帳戶
   */
  private handleAccountsChanged = async (accounts: string[]) => {
    const currWalletType = this.localStorageService.getConnectedWeb3WalletType();

    // 只有在 MetaMask 連線狀態下才處理帳號切換
    if (this._isConnected.getValue() && currWalletType === Web3WalletType.MetaMask) {
      if (accounts.length === 0) {
        // 如果陣列為空，代表使用者已在錢包中斷開所有帳戶的連接
        await this.disconnectWallet();
      } else {
        const curAccounts = this._accounts.getValue();

        // 判斷已經有連結帳戶，且當前帳戶與新帳戶不同
        if (curAccounts[0] && curAccounts[0] !== accounts[0]) {
          // 如果帳戶變更，直接登出
          await this.disconnectWallet();
          this.appService.logoutSubject$.next(true);
        }
      }
    }
  };

  /**
   * 處理網路鏈變更事件
   * @param chainIdHex - 新的鏈 ID (格式為 0x 開頭的十六進制字符串)
   */
  private handleChainChanged = (chainIdHex: string) => {
    const currWalletType = this.localStorageService.getConnectedWeb3WalletType();

    // 只有在 MetaMask 連線狀態下才處理帳號切換
    if (this._isConnected.getValue() && currWalletType === Web3WalletType.MetaMask) {
      const chainIdDec = parseInt(chainIdHex, 16);
      this._currentChainId.next(chainIdDec);
    }
  };

  /**
   * 移除監聽事件
   */
  ngOnDestroy(): void {}

  /**
   * 更新連線狀態
   * @param isConnected - 是否已連線
   */
  updateConnectedStatus(isConnected: boolean) {
    this._isConnected.next(isConnected);
  }

  // 檢查已存在的連接
  async checkExistingConnection(): Promise<boolean> {
    if (!this.provider) {
      console.warn('Provider not initialized when checking existing connection');
      return false;
    }

    try {
      const existingAccounts = (await this.provider.request({ method: 'eth_accounts' })) as string[];
      const isLogin = Boolean(this.localStorageService.loginToken);

      if (existingAccounts && existingAccounts.length > 0 && isLogin) {
        this._accounts.next(existingAccounts);

        // Reload後，判斷已連線的web3錢包是否為綁定於DB的web3錢包
        const web3WalletRes = await firstValueFrom(this.accountApi.getWeb3Wallet());
        const connectedWallets = web3WalletRes.data || [];
        const isExistingWallet = connectedWallets.some(
          wallet => wallet.address.toLowerCase() === existingAccounts[0].toLowerCase(),
        );

        this.updateConnectedStatus(isExistingWallet);
        await this.fetchCurrentChainId(); // 獲取當前的 Chain ID

        return isExistingWallet;
      }
    } catch (err) {
      console.error('Error checking existing connection in service:', err);
    }

    return false;
  }

  /**
   * 請求連接到 MetaMask
   */
  async connectWallet() {
    try {
      if (this.client.status === 'connecting' && this.curDeepLink) {
        // window.open(this.myDeepLink, '_self');
        // window.close();

        window.location.href = this.curDeepLink;
      } else {
        await this.client.connect({
          chainIds: this.supportedNetworkChainIds,
          forceRequest: true, // 強制每次都彈出連接請求，確保獲取最新的帳戶資訊
        });
      }

      const accounts = this.client.accounts;
      this.curDeepLink = ''; // 連接成功後清除 deeplink

      if (accounts && accounts.length > 0) {
        this._accounts.next(accounts);
        this.updateConnectedStatus(false);
        // 連接成功後，獲取一次當前的 Chain ID
        await this.fetchCurrentChainId();
      }
    } catch (error: any) {
      if (error.code === 4001) {
        console.error('User rejected the connection request');
      } else if (error.code === -32002) {
        console.error('Connection request already pending');
      } else {
        console.error('Connection failed:', error);
      }

      // 連接失敗或被拒絕，確保狀態重置
      await this.disconnectWallet();
    }
  }

  /**
   * 斷開與錢包的連接並重置狀態
   */
  async disconnectWallet() {
    if (this.client && this.client.disconnect) {
      try {
        await this.client.disconnect();
        window.location.reload(); // 斷開連接後重新載入頁面，確保狀態完全重置
      } catch (err) {
        console.error('Error disconnecting wallet:', err);
      }
    }

    // 重置所有狀態
    this._accounts.next([]);
    this.updateConnectedStatus(false);
    this._currentChainId.next(null);
  }

  /**
   * 簽署消息
   * @param message
   * @returns 簽章資訊或 null
   */
  async signMessage(message: string): Promise<Web3WalletSignatureInfo | null> {
    if (!this.provider || this._accounts.getValue().length === 0) {
      return null;
    }

    const hexMessage = '0x' + Buffer.from(message, 'utf8').toString('hex');
    const signerAddress = this._accounts.getValue()[0];

    const signature = (await this.provider.request({
      method: 'personal_sign',
      params: [hexMessage, signerAddress],
    })) as string;

    // 連接成功後，獲取當前的 Chain ID
    const chainId = await this.fetchCurrentChainId();

    if (chainId) {
      return {
        walletType: Web3WalletType.MetaMask,
        network: chainId.toString(),
        address: signerAddress,
        signature,
      };
    }

    return null;
  }

  /**
   * 獲取當前連接的網路 Chain ID
   * @returns 當前 Chain ID，無法取得時為 null
   */
  async fetchCurrentChainId() {
    if (!this.provider) {
      this._currentChainId.next(null); // 清除舊值
      return null;
    }

    try {
      // 使用 EIP-1193 標準方法 eth_chainId
      const chainIdHex = (await this.provider.request({ method: 'eth_chainId' })) as string;
      const chainIdDec = parseInt(chainIdHex, 16); // 將 16 進制轉為 10 進制
      this._currentChainId.next(chainIdDec);

      return chainIdDec;
    } catch (err) {
      console.error('Fetch Chain ID failed:', err);
      return null;
    }
  }

  /**
   * 獲取第一個連接帳戶的餘額
   * @returns 帳戶餘額（ETH單位）或 null
   */
  async fetchBalance() {
    const currentAccounts = this._accounts.getValue();
    if (!this.provider || !this._isConnected.getValue() || currentAccounts.length === 0) {
      return null;
    }

    try {
      const address = currentAccounts[0]; // 獲取第一個帳戶地址

      // 用 MetaMask provider 初始化 ethers provider
      const ethersProvider = new ethers.BrowserProvider(this.provider);
      const balanceWei = await ethersProvider.getBalance(address);
      const formattedBalance = ethers.formatEther(balanceWei);

      return formattedBalance;
    } catch (err) {
      console.error('Fetch balance error:', err);
      return null;
    }
  }

  /**
   * 獲取合約帳戶的餘額
   * @param network 虛擬貨幣網路資訊
   * @returns 合約帳戶餘額或 null
   */
  async fetchContractBalance(network: NetworksInterface) {
    const currentAccounts = this._accounts.getValue();
    if (!this.provider || !this._isConnected.getValue() || currentAccounts.length === 0) {
      return null;
    }

    try {
      // 使用 Infura RPC 初始化 ethers provider
      // const hexChainId = ('0x' + Number(network.chainId).toString(16)) as `0x${string}`; // 將 chainId 轉換為十六進制
      // const rpcUrl = getInfuraRpcUrls({ infuraApiKey: INFURA_API_KEY })[hexChainId];
      // const ethersProvider = new ethers.JsonRpcProvider(rpcUrl);

      // 用 MetaMask provider 初始化 ethers provider
      const ethersProvider = new ethers.BrowserProvider(this.provider);

      const address = currentAccounts[0]; // 獲取第一個帳戶地址
      const { contractAddress, contractABI, decimals } = network;
      const contract = new ethers.Contract(contractAddress, contractABI, ethersProvider);
      const curBalanceWei = await contract.balanceOf(address);
      const finalBalance = ethers.formatUnits(curBalanceWei, decimals);

      return finalBalance.toString();
    } catch (err) {
      console.error('Fetch contract balance error:', err);
      return null;
    }
  }

  /**
   * 編碼轉帳合約 ABI
   * @param network 虛擬貨幣網路資訊
   * @param toAddress 接收方地址
   * @param amount 轉帳金額
   * @returns 編碼後的合約 ABI 字符串
   */
  transferContractABI(network: NetworksInterface, toAddress: string, amount: string) {
    const { contractABI, decimals } = network;

    // 使用 ethers.utils.parseUnits 來處理數量轉換
    // 它會回傳一個 BigNumber 物件，最後再轉成字串
    const amountToSend = ethers.parseUnits(amount, decimals).toString();

    // 使用 ethers.js 的 Interface 來編碼合約 ABI
    const iface = new Interface(JSON.parse(contractABI));
    // 編碼 transfer 函數的data
    const data = iface.encodeFunctionData('transfer', [toAddress, amountToSend]);

    return data;
  }

  /**
   * 發送交易 (例如：發送 ETH)
   * @param transactionParameters
   * @returns 交易hash 或 null
   */
  private async _sendTransaction(transactionParameters: Web3TransactionConfig) {
    if (!this._isConnected.getValue() || !this.provider) {
      return null;
    }

    const showErrorMsg = () => {
      this.toastService.show({
        title: 'Deposit request failed',
        message: 'You rejected the transaction request',
        type: 'fail',
      });
    };

    try {
      // 使用 eth_sendTransaction 方法
      const txHash = await this.provider
        .request({
          method: 'eth_sendTransaction',
          params: [transactionParameters],
        })
        .catch((err: any) => {
          showErrorMsg();
          console.error('MetaMask Error sending transaction:', err);
          return null;
        });

      return txHash;
    } catch (err) {
      showErrorMsg();
      console.error('MetaMask Transaction failed:', err);
      return null;
    }
  }

  async sendMainTransaction(toAddress: string, amount: string): Promise<string | null> {
    const currentAccounts = this._accounts.getValue();
    if (!this._isConnected.getValue() || !this.provider || currentAccounts.length === 0) {
      return null;
    }

    const fromAddress = currentAccounts[0];

    // 將 Ether 金額轉換為 Wei (BigInt)
    const valueWei = ethers.parseEther(amount);

    const transactionParameters: Web3TransactionConfig = {
      from: fromAddress,
      to: toAddress,
      value: '0x' + valueWei.toString(16), // 轉換為十六進制字符串
    };

    return this._sendTransaction(transactionParameters) as Promise<string | null>;
  }

  async sendContractTransaction(toAddress: string, amount: string, network: NetworksInterface): Promise<string | null> {
    const currentAccounts = this._accounts.getValue();
    if (!this._isConnected.getValue() || !this.provider || currentAccounts.length === 0) {
      return null;
    }

    const fromAddress = currentAccounts[0];

    // 將 Ether 金額轉換為 Wei (BigInt)
    const valueWei = ethers.parseEther('0');

    const transactionParameters: Web3TransactionConfig = {
      from: fromAddress,
      to: network.contractAddress,
      value: '0x' + valueWei.toString(16), // 轉換為十六進制字符串
      data: this.transferContractABI(network, toAddress, amount),
    };

    return this._sendTransaction(transactionParameters) as Promise<string | null>;
  }

  /**
   * 切換到指定的網路
   * @param chainId 要切換到的 Chain ID
   * @returns Promise that resolves when the network switch completes or rejects on error
   */
  async switchNetwork(chainId: number | string | null): Promise<void> {
    if (!this._isConnected.getValue() || !this.provider || chainId === null) {
      return;
    }

    const showErrorMsg = () => {
      this.toastService.show({
        title: 'Network not detected',
        message:
          'This network is not enabled in your wallet. Please enable it and try again or select a different network.',
        type: 'fail',
      });
    };

    // 檢查是否支援該網路
    if (!this.supportedNetworks.includes(Number(chainId))) {
      showErrorMsg();
      console.error('Failed to switch network: Unsupported network');
      return Promise.reject('Unsupported network');
    }

    try {
      const hexChainId = '0x' + Number(chainId).toString(16); // 將 chainId 轉換為十六進制

      await this.provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexChainId }],
      });
    } catch (err) {
      // 顯示網路切換失敗
      showErrorMsg();
      console.error('Failed to switch network:', err);
      return Promise.reject(err);
    }
  }
}
